<template>
  <div :class="rootClass || 'block w-full'">
    <!-- Optional caption above the trigger -->
    <p v-if="label" class="text-xs text-gray-500 dark:text-gray-400 mb-1">{{ label }}</p>

    <div class="relative select-none" ref="containerRef" @click.stop>
      <!-- Trigger Button (always white — accent colors are for primary actions) -->
      <button
        ref="triggerRef"
        type="button"
        @click="toggleDropdown"
        @keydown="handleTriggerKeyDown"
        :disabled="disabled"
        class="flex items-center justify-between gap-1.5 rounded-xl border text-sm font-medium transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500/20 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
        :class="[
          buttonClass || 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 px-3 py-2.5 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white w-full',
          isOpen ? 'ring-2 ring-indigo-500/30 border-indigo-500/50' : '',
          hasError ? '!border-rose-500' : ''
        ]"
        :title="title || selectedLabel"
        :aria-haspopup="'listbox'"
        :aria-expanded="isOpen"
      >
        <span class="flex items-center gap-1.5 min-w-0 truncate">
          <span v-if="icon" class="text-sm shrink-0 leading-none select-none">{{ icon }}</span>
          <span class="truncate" :class="{ 'text-gray-400 dark:text-gray-500': !hasSelection }">
            {{ selectedLabel }}
          </span>
        </span>

        <svg
          class="w-4 h-4 shrink-0 text-gray-400 transition-transform duration-200"
          :class="isOpen ? 'rotate-180 text-indigo-600 dark:text-indigo-400' : ''"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      <!-- Dropdown Menu (abre instantâneo: transição de entrada criava
        janela onde cliques rápidos caíam em botões ainda animando/invisíveis,
        exigindo 2ª/3ª tentativa) -->
      <div
        v-if="isOpen"
        class="absolute z-[999] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl py-1 max-h-60 overflow-y-auto focus:outline-none text-sm"
          :class="[
            dropUp ? 'bottom-full mb-1' : 'top-full mt-1',
            align === 'right' ? 'right-0' : 'left-0',
            menuClass || 'w-full min-w-[140px]'
          ]"
          role="listbox"
        >
          <!-- Grouped Options -->
          <template v-if="groups && groups.length > 0">
            <div v-for="group in groups" :key="group.label" class="py-1 first:pt-0 last:pb-0">
              <div class="px-3 py-1 text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider bg-gray-50/70 dark:bg-gray-900/30 select-none">
                {{ group.label }}
              </div>
              <button
                type="button"
                v-for="opt in group.options"
                :key="getOptionValue(opt)"
                @click="selectOption(getOptionValue(opt))"
                @mousemove="highlighted = String(getOptionValue(opt))"
                class="w-full flex items-center justify-between gap-2 px-3 py-2 text-left transition-colors cursor-pointer"
                :class="optionClass(getOptionValue(opt))"
                role="option"
                :aria-selected="isSelected(getOptionValue(opt))"
              >
                <span class="truncate">{{ getOptionLabel(opt) }}</span>
                <CheckIcon v-if="isSelected(getOptionValue(opt))" />
              </button>
            </div>
          </template>

          <!-- Flat Options -->
          <template v-else>
            <button
              type="button"
              v-for="opt in normalizedOptions"
              :key="opt.value"
              @click="selectOption(opt.value)"
              @mousemove="highlighted = String(opt.value)"
              class="w-full flex items-center justify-between gap-2 px-3 py-2 text-left transition-colors cursor-pointer"
              :class="optionClass(opt.value)"
              role="option"
              :aria-selected="isSelected(opt.value)"
            >
              <span class="truncate">{{ opt.label }}</span>
              <CheckIcon v-if="isSelected(opt.value)" />
            </button>
            <p v-if="normalizedOptions.length === 0" class="px-3 py-2 text-gray-400 dark:text-gray-500 text-xs">
              {{ emptyText }}
            </p>
          </template>
        </div>
    </div>

    <!-- Optional error / hint line -->
    <p v-if="error" class="text-xs text-rose-500 mt-1">{{ error }}</p>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, defineProps, defineEmits, h } from 'vue';

const CheckIcon = (props, { attrs }) => h('svg', {
  class: 'w-4 h-4 shrink-0 text-indigo-600 dark:text-indigo-400 ml-2',
  fill: 'none',
  stroke: 'currentColor',
  viewBox: '0 0 24 24',
  ...attrs,
}, [h('path', {
  'stroke-linecap': 'round',
  'stroke-linejoin': 'round',
  'stroke-width': '2.5',
  d: 'M5 13l4 4L19 7',
})]);

const props = defineProps({
  modelValue: {
    type: [String, Number, Boolean],
    default: '',
  },
  options: {
    type: Array,
    default: () => [],
  },
  groups: {
    type: Array,
    default: null,
  },
  label: {
    type: String,
    default: '',
  },
  icon: {
    type: String,
    default: '',
  },
  title: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: 'Selecione...',
  },
  emptyText: {
    type: String,
    default: 'Nenhuma opção',
  },
  error: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  align: {
    type: String,
    default: 'left', // 'left' | 'right'
  },
  rootClass: {
    type: String,
    default: '',
  },
  buttonClass: {
    type: String,
    default: '',
  },
  menuClass: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:modelValue', 'change']);

const isOpen = ref(false);
const dropUp = ref(false);
const highlighted = ref(null);
const containerRef = ref(null);
const triggerRef = ref(null);

const normalizedOptions = computed(() => {
  return props.options.map((opt) => {
    if (typeof opt === 'object' && opt !== null) {
      return {
        value: opt.value !== undefined ? opt.value : opt.id,
        label: opt.label !== undefined ? opt.label : (opt.name || opt.value),
      };
    }
    return { value: opt, label: String(opt) };
  });
});

const flatValues = computed(() => {
  if (props.groups && props.groups.length > 0) {
    return props.groups.flatMap((g) => (g.options || []).map(getOptionValue));
  }
  return normalizedOptions.value.map((o) => o.value);
});

const getOptionValue = (opt) => {
  if (typeof opt === 'object' && opt !== null) {
    return opt.value !== undefined ? opt.value : opt.id;
  }
  return opt;
};

const getOptionLabel = (opt) => {
  if (typeof opt === 'object' && opt !== null) {
    return opt.label !== undefined ? opt.label : (opt.name || opt.value);
  }
  return String(opt);
};

const isSelected = (val) => String(val) === String(props.modelValue);
const hasSelection = computed(() => String(props.modelValue ?? '') !== '');

const hasError = computed(() => !!props.error);

const selectedLabel = computed(() => {
  if (props.groups && props.groups.length > 0) {
    for (const grp of props.groups) {
      const found = (grp.options || []).find((opt) => isSelected(getOptionValue(opt)));
      if (found) return getOptionLabel(found);
    }
  }
  const found = normalizedOptions.value.find((opt) => isSelected(opt.value));
  if (found) return found.label;
  return props.placeholder || '';
});

const optionClass = (val) => {
  const key = String(val);
  if (isSelected(val)) {
    return 'bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400 font-semibold';
  }
  if (highlighted.value === key) {
    return 'text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-700';
  }
  return 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700';
};

const toggleDropdown = () => {
  if (props.disabled) return;
  if (!isOpen.value && containerRef.value) {
    const rect = containerRef.value.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    dropUp.value = spaceBelow < 220 && rect.top > 220;
    highlighted.value = String(props.modelValue ?? '');
  }
  isOpen.value = !isOpen.value;
};

const selectOption = (val) => {
  emit('update:modelValue', val);
  emit('change', val);
  isOpen.value = false;
  triggerRef.value?.focus?.();
};

const moveHighlight = (dir) => {
  const values = flatValues.value.map(String);
  if (values.length === 0) return;
  let idx = values.indexOf(highlighted.value);
  idx = idx === -1 ? (dir > 0 ? 0 : values.length - 1) : (idx + dir + values.length) % values.length;
  highlighted.value = values[idx];
};

const handleTriggerKeyDown = (event) => {
  if (props.disabled) return;
  if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key) && !isOpen.value) {
    event.preventDefault();
    toggleDropdown();
    return;
  }
  if (!isOpen.value) return;
  if (event.key === 'ArrowDown') {
    event.preventDefault();
    moveHighlight(1);
  } else if (event.key === 'ArrowUp') {
    event.preventDefault();
    moveHighlight(-1);
  } else if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    if (highlighted.value !== null) selectOption(highlighted.value);
  } else if (event.key === 'Escape') {
    isOpen.value = false;
  } else if (event.key === 'Tab') {
    isOpen.value = false;
  }
};

const handleClickOutside = (event) => {
  if (containerRef.value && !containerRef.value.contains(event.target)) {
    isOpen.value = false;
  }
};

onMounted(() => {
  document.addEventListener('pointerdown', handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', handleClickOutside);
});
</script>
